<template>
  <v-dialog v-model="innerValue" persistent max-width="520px" content-class="start-order-dialog">
    <v-card class="start-card">
      <div class="start-dialog-header">
        <div class="step-icon">
          <v-icon color="primary">{{ step === "phone" ? (loginMethod === "phone" ? "mdi-phone-outline" : "mdi-email-outline") : step === "profile" ? "mdi-account-plus-outline" : step === "locations" ? "mdi-map-marker-multiple-outline" : "mdi-message-lock-outline" }}</v-icon>
        </div>
        <div class="header-copy">
          <div class="step-label">{{ recognizing ? "WELCOME BACK" : step === "locations" ? "CHOOSE LOCATION" : step === "profile" ? "CREATE ACCOUNT" : "SECURE LOGIN" }}</div>
          <div class="start-dialog-title">{{ recognizing ? "Recognizing This Browser" : step === "phone" ? "Log In" : step === "profile" ? "Tell Us About You" : step === "locations" ? "Where Is This Order For?" : `Verify Your ${loginMethod === "phone" ? "Phone" : "Email"}` }}</div>
          <div class="start-dialog-subtitle">
            <template v-if="recognizing">Checking your saved verification.</template>
            <template v-else-if="step === 'phone'">Choose how you want to receive your verification code.</template>
            <template v-else-if="step === 'otp'">We sent a code to <strong>{{ verificationDestination }}</strong>.</template>
            <template v-else-if="step === 'profile'">Your {{ loginMethod === "phone" ? "phone" : "email" }} is verified. Complete your new account.</template>
            <template v-else>Select one of the locations assigned to your login.</template>
          </div>
        </div>
        <v-btn icon small aria-label="Close" @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </div>

      <v-card-text class="start-dialog-body">
        <div v-if="recognizing" class="recognizing"><v-progress-circular indeterminate color="primary" /><span>Opening your order…</span></div>
        <template v-else-if="step === 'phone'">
          <div class="login-method-label">LOG IN USING</div>
          <v-btn-toggle v-model="loginMethod" mandatory rounded class="login-method-toggle" @change="changeLoginMethod">
            <v-btn
              value="phone"
              :color="loginMethod === 'phone' ? buttonColor : undefined"
              :dark="loginMethod === 'phone' && darkMode"
            ><v-icon left small>mdi-cellphone</v-icon>Cell Phone</v-btn>
            <v-btn
              value="email"
              :color="loginMethod === 'email' ? buttonColor : undefined"
              :dark="loginMethod === 'email' && darkMode"
            ><v-icon left small>mdi-email-outline</v-icon>Email</v-btn>
          </v-btn-toggle>
          <v-text-field
            v-if="loginMethod === 'phone'"
            v-model.trim="form.phoneNumber"
            label="Mobile Phone Number"
            type="tel"
            inputmode="tel"
            autocomplete="tel"
            placeholder="(555) 555-1234"
            outlined
            :error-messages="errors.phoneNumber"
            hint="We will text this number a verification code."
            persistent-hint
            autofocus
            @keyup.enter="sendCode"
          />
          <v-text-field
            v-else
            v-model.trim="form.email"
            label="Email Address"
            type="email"
            inputmode="email"
            autocomplete="email"
            placeholder="you@business.com"
            outlined
            :error-messages="errors.email"
            hint="We will email this address a verification code."
            persistent-hint
            autofocus
            @keyup.enter="sendCode"
          />
        </template>

        <template v-else-if="step === 'profile'">
          <v-text-field v-model.trim="form.name" label="Your Name" autocomplete="name" outlined :error-messages="errors.name" autofocus />
          <v-text-field v-model.trim="form.businessName" label="Business Name" autocomplete="organization" outlined :error-messages="errors.businessName" />
          <v-text-field
            v-if="loginMethod === 'phone'"
            v-model.trim="form.email"
            label="Email Address"
            type="email"
            inputmode="email"
            autocomplete="email"
            outlined
            :error-messages="errors.email"
            @keyup.enter="createAccount"
          />
        </template>

        <template v-else-if="step === 'otp'">
          <div class="otp-copy">Enter the 6-digit code. You will continue automatically after the last digit.</div>
          <v-otp-input
            ref="otpInput"
            v-model="code"
            class="otp-input"
            :length="6"
            type="number"
            @finish="verifyCode"
          />
          <div v-if="otpError.length" class="otp-error" role="alert">{{ otpError[0] }}</div>
          <div class="otp-keypad" aria-label="Verification code keypad">
            <v-btn v-for="digit in keypadDigits" :key="digit" class="keypad-key" outlined @click="appendDigit(digit)">{{ digit }}</v-btn>
            <span />
            <v-btn class="keypad-key" outlined @click="appendDigit('0')">0</v-btn>
            <v-btn class="keypad-key" text aria-label="Delete last digit" @click="deleteDigit"><v-icon>mdi-backspace-outline</v-icon></v-btn>
          </div>
          <div class="verification-actions">
            <v-btn text small color="primary" @click="editContact"><v-icon left small>mdi-pencil</v-icon>Change {{ loginMethod === "phone" ? "number" : "email" }}</v-btn>
            <v-btn text small color="primary" :disabled="resendSeconds > 0 || sendingCode" :loading="sendingCode" @click="resendCode">
              {{ resendSeconds > 0 ? `Resend in ${formatCountdown(resendSeconds)}` : "Resend code" }}
            </v-btn>
          </div>
          <v-alert dense text type="info" class="privacy-note">{{ loginMethod === "phone" ? "Message and data rates may apply. " : "" }}The code expires in 10 minutes.</v-alert>
        </template>
        <v-radio-group v-else-if="step === 'locations'" v-model="selectedCustomerId" class="location-list" hide-details>
          <v-radio v-for="customer in assignedCustomers" :key="customer.id" :value="customer.id">
            <template v-slot:label>
              <div class="location-option"><strong>{{ customer.name }}</strong><span>{{ formatLocation(customer) }}</span></div>
            </template>
          </v-radio>
        </v-radio-group>
      </v-card-text>

      <v-divider v-if="!recognizing" />
      <v-card-actions v-if="!recognizing" class="start-dialog-actions">
        <v-btn text @click="close">Cancel</v-btn>
        <v-spacer />
        <v-btn
          rounded
          large
          :dark="darkMode"
          :color="buttonColor"
          :loading="loading || sendingCode || verifying || creatingAccount"
          :disabled="(step === 'otp' && code.length !== 6) || (step === 'locations' && !selectedCustomerId)"
          @click="step === 'phone' ? sendCode() : step === 'profile' ? createAccount() : step === 'locations' ? continueWithLocation() : verifyCode()"
        >{{ step === "phone" ? "Send Code" : step === "profile" ? "Create Account" : step === "locations" ? "Start Order" : "Verify & Continue" }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import orderService from "./service";

export default {
  name: "PublicCustomerStartDialog",
  props: {
    value: { type: Boolean, default: false },
    businessCode: { type: [String, Number], required: true },
    buttonColor: { type: String, default: "primary" },
    darkMode: { type: Boolean, default: true },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      step: "phone",
      loginMethod: "phone",
      form: { name: "", businessName: "", phoneNumber: "", email: "" },
      errors: { name: [], businessName: [], phoneNumber: [], email: [] },
      code: "",
      otpError: [],
      challengeId: null,
      phoneLast4: "",
      verificationDestination: "",
      resendSeconds: 0,
      countdownTimer: null,
      sendingCode: false,
      verifying: false,
      creatingAccount: false,
      recognizing: false,
      assignedCustomers: [],
      selectedCustomerId: null,
      verifiedSession: null,
      keypadDigits: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    };
  },
  computed: {
    innerValue: {
      get() { return this.value; },
      set(value) { this.$emit("input", value); },
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(open) { if (open) this.openDialog(); },
    },
  },
  created() {
    const saved = localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}publicOrderCustomer`);
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      this.form = {
        name: parsed.name || "",
        businessName: parsed.businessName || parsed.name || "",
        phoneNumber: parsed.phoneNumber || "",
        email: parsed.email || "",
      };
    } catch (error) {
      localStorage.removeItem(`${process.env.VUE_APP_STORAGE_PREFIX}publicOrderCustomer`);
    }
  },
  beforeDestroy() { clearInterval(this.countdownTimer); },
  methods: {
    close() {
      const session = this.verifiedSession;
      this.innerValue = false;
      this.resetVerification();
      if (session && session.profileComplete && (session.assignedCustomers || []).length) this.$emit("authenticated", session);
    },
    trustedTokenKey() { return `${process.env.VUE_APP_STORAGE_PREFIX}publicOrderTrustedDevice:${this.businessCode}`; },
    readTrustedToken() {
      const stored = localStorage.getItem(this.trustedTokenKey());
      if (stored) return stored;
      const name = `${this.trustedTokenKey()}=`;
      const cookie = document.cookie.split("; ").find(item => item.startsWith(name));
      return cookie ? decodeURIComponent(cookie.slice(name.length)) : "";
    },
    saveTrustedToken(token) {
      localStorage.setItem(this.trustedTokenKey(), token);
      document.cookie = `${this.trustedTokenKey()}=${encodeURIComponent(token)}; Max-Age=315360000; Path=/; SameSite=Lax`;
    },
    clearTrustedToken() {
      localStorage.removeItem(this.trustedTokenKey());
      document.cookie = `${this.trustedTokenKey()}=; Max-Age=0; Path=/; SameSite=Lax`;
    },
    async openDialog() {
      this.resetVerification();
      const token = this.readTrustedToken();
      if (!token) return;
      this.recognizing = true;
      try {
        const response = await orderService.recognizePublicOrderBrowser(this.businessCode, token);
        localStorage.setItem(`${process.env.VUE_APP_STORAGE_PREFIX}publicOrderCustomer`, JSON.stringify(response.data.customerInfo));
        this.form = { ...this.form, ...(response.data.customerInfo || {}) };
        this.loginMethod = response.data.verificationMethod || (this.form.email && !this.form.phoneNumber ? "email" : "phone");
        if (!response.data.profileComplete) {
          this.verifiedSession = response.data;
          this.step = "profile";
        } else this.finishOrChooseLocation(response.data);
      } catch (error) {
        this.clearTrustedToken();
      } finally {
        this.recognizing = false;
      }
    },
    resetVerification() {
      this.step = "phone";
      this.code = "";
      this.otpError = [];
      this.challengeId = null;
      this.resendSeconds = 0;
      clearInterval(this.countdownTimer);
      this.recognizing = false;
      this.assignedCustomers = [];
      this.selectedCustomerId = null;
      this.verifiedSession = null;
    },
    phoneDigits() { return String(this.form.phoneNumber || "").replace(/\D/g, ""); },
    validatePhone() {
      this.errors = { name: [], businessName: [], phoneNumber: [], email: [] };
      const digits = this.phoneDigits();
      if (!(digits.length === 10 || (digits.length === 11 && digits.startsWith("1")))) {
        this.errors.phoneNumber = ["Enter a valid 10-digit US mobile number"];
      }
      return !Object.values(this.errors).some(messages => messages.length);
    },
    validateEmail() {
      this.errors = { name: [], businessName: [], phoneNumber: [], email: [] };
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email.trim())) this.errors.email = ["Enter a valid email address"];
      return !this.errors.email.length;
    },
    validateProfile() {
      this.errors = { name: [], businessName: [], phoneNumber: [], email: [] };
      if (this.form.name.trim().length < 2) this.errors.name = ["Enter your full name"];
      if (this.form.businessName.trim().length < 2) this.errors.businessName = ["Enter a valid business name"];
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email.trim())) this.errors.email = ["Enter a valid email address"];
      return !Object.values(this.errors).some(messages => messages.length);
    },
    async sendCode() {
      if (this.loginMethod === "phone" ? !this.validatePhone() : !this.validateEmail()) return;
      this.sendingCode = true;
      try {
        const response = await orderService.sendPublicOrderVerification(this.businessCode, {
          verificationMethod: this.loginMethod,
          phoneNumber: this.loginMethod === "phone" ? this.form.phoneNumber : undefined,
          email: this.loginMethod === "email" ? this.form.email : undefined,
        });
        this.challengeId = response.data.challengeId;
        this.phoneLast4 = response.data.phoneLast4;
        this.verificationDestination = response.data.destination || (this.loginMethod === "phone" ? `phone ending in ${this.phoneLast4}` : this.form.email);
        this.step = "otp";
        this.startCountdown(response.data.resendAfterSeconds || 120);
        this.$nextTick(() => this.$refs.otpInput && this.$refs.otpInput.focus());
      } catch (error) {
        const data = error.response && error.response.data;
        const message = (data && data.message) || `Unable to send a code to this ${this.loginMethod === "phone" ? "number" : "email address"}`;
        if (this.step === "otp") this.otpError = [message];
        else this.errors[this.loginMethod === "phone" ? "phoneNumber" : "email"] = [message];
        if (data && data.retryAfterSeconds) this.startCountdown(data.retryAfterSeconds);
      } finally {
        this.sendingCode = false;
      }
    },
    async resendCode() { await this.sendCode(); },
    appendDigit(digit) {
      if (this.verifying || this.code.length >= 6) return;
      this.code = `${this.code}${digit}`;
      this.otpError = [];
      if (this.code.length === 6) this.$nextTick(this.verifyCode);
    },
    deleteDigit() { if (!this.verifying) this.code = this.code.slice(0, -1); },
    async verifyCode() {
      if (this.code.length !== 6) { this.otpError = ["Enter the 6-digit code"]; return; }
      this.verifying = true;
      try {
        const response = await orderService.verifyPublicOrderPhone(this.businessCode, {
          challengeId: this.challengeId,
          verificationMethod: this.loginMethod,
          phoneNumber: this.loginMethod === "phone" ? this.form.phoneNumber : undefined,
          email: this.loginMethod === "email" ? this.form.email : undefined,
          code: this.code,
        });
        const customerInfo = response.data.customerInfo || { phoneNumber: response.data.phoneNumber };
        this.form = { ...this.form, ...customerInfo };
        localStorage.setItem(`${process.env.VUE_APP_STORAGE_PREFIX}publicOrderCustomer`, JSON.stringify(customerInfo));
        this.saveTrustedToken(response.data.trustedDeviceToken);
        const session = { ...response.data, customerInfo };
        if (!response.data.profileComplete) {
          this.verifiedSession = session;
          this.step = "profile";
        } else this.finishOrChooseLocation(session);
      } catch (error) {
        const data = error.response && error.response.data;
        this.otpError = [(data && data.message) || "The verification code is incorrect"];
      } finally {
        this.verifying = false;
      }
    },
    editContact() { this.step = "phone"; this.code = ""; this.otpError = []; },
    changeLoginMethod() {
      this.code = "";
      this.challengeId = null;
      this.verificationDestination = "";
      this.errors = { name: [], businessName: [], phoneNumber: [], email: [] };
    },
    async createAccount() {
      if (!this.validateProfile() || !this.verifiedSession) return;
      this.creatingAccount = true;
      try {
        const response = await orderService.completePublicOrderProfile(this.businessCode, { verificationToken: this.verifiedSession.verificationToken, name: this.form.name, businessName: this.form.businessName, email: this.form.email });
        localStorage.setItem(`${process.env.VUE_APP_STORAGE_PREFIX}publicOrderCustomer`, JSON.stringify(response.data.customerInfo));
        this.finishOrChooseLocation(response.data);
      } catch (error) {
        const data = error.response && error.response.data;
        this.errors.email = [(data && data.message) || "Unable to create your account"];
      } finally { this.creatingAccount = false; }
    },
    finishOrChooseLocation(session) {
      const assigned = session.assignedCustomers || [];
      if (!assigned.length) {
        this.$emit("continue", session);
        this.innerValue = false;
        return;
      }
      this.verifiedSession = session;
      this.assignedCustomers = assigned;
      this.selectedCustomerId = assigned.length === 1 ? assigned[0].id : null;
      this.step = "locations";
    },
    continueWithLocation() {
      const customer = this.assignedCustomers.find(row => Number(row.id) === Number(this.selectedCustomerId));
      if (!customer || !this.verifiedSession) return;
      const customerInfo = {
        name: customer.name,
        businessName: customer.name,
        phoneNumber: customer.phoneNumber || customer.cellNumber || this.verifiedSession.customerInfo.phoneNumber,
        email: customer.email || this.verifiedSession.customerInfo.email,
        CustomerId: customer.id,
      };
      this.$emit("continue", { ...this.verifiedSession, customerInfo });
      this.innerValue = false;
    },
    formatLocation(customer) {
      return [customer.address, customer.address1, customer.city, customer.state, customer.zip].filter(Boolean).join(", ") || customer.phoneNumber || customer.cellNumber || "Location details unavailable";
    },
    startCountdown(seconds) {
      this.resendSeconds = Number(seconds || 0);
      clearInterval(this.countdownTimer);
      this.countdownTimer = setInterval(() => {
        if (this.resendSeconds <= 1) { this.resendSeconds = 0; clearInterval(this.countdownTimer); }
        else this.resendSeconds -= 1;
      }, 1000);
    },
    formatCountdown(seconds) { return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`; },
  },
};
</script>

<style scoped>
.start-card{border-radius:22px!important;overflow:hidden}.start-dialog-header{padding:20px;display:flex;align-items:flex-start;gap:12px;border-bottom:1px solid #e2e8f0}.step-icon{width:44px;height:44px;flex:none;border-radius:14px;background:#eff6ff;display:flex;align-items:center;justify-content:center}.header-copy{flex:1;min-width:0}.step-label,.login-method-label{font-size:10px;font-weight:900;letter-spacing:.12em;color:#64748b}.start-dialog-title{font-size:21px;font-weight:900;color:#0f172a}.start-dialog-subtitle{font-size:13px;line-height:1.45;color:#64748b;margin-top:3px}.start-dialog-body{padding:20px!important}.login-method-label{margin-bottom:7px}.login-method-toggle{display:flex!important;width:100%;margin-bottom:20px}.login-method-toggle .v-btn{flex:1;opacity:1!important;transition:background-color .18s ease,color .18s ease,box-shadow .18s ease}.login-method-toggle .v-btn--active{box-shadow:0 4px 12px rgba(15,23,42,.2)!important;font-weight:800}.recognizing{min-height:180px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;color:#475569;font-weight:700}.otp-copy{text-align:center;color:#475569;font-size:14px;margin:4px 0 14px}.otp-input{max-width:420px;margin:0 auto}.otp-error{text-align:center;color:#d32f2f;font-size:13px;margin:8px 0}.otp-keypad{max-width:330px;margin:16px auto 6px;display:grid;grid-template-columns:repeat(3,1fr);gap:10px}.keypad-key{height:54px!important;font-size:22px!important;font-weight:800!important;border-radius:12px!important}.verification-actions{display:flex;justify-content:space-between;align-items:center;margin-top:10px}.privacy-note{font-size:12px;margin:16px 0 0}.location-list{max-height:340px;overflow:auto}.location-option{display:flex;flex-direction:column;padding:9px 0;color:#0f172a}.location-option span{font-size:13px;color:#64748b;margin-top:3px}.start-dialog-actions{padding:14px 20px!important;background:#f8fafc}@media(max-width:600px){.start-order-dialog{margin:8px!important}.start-card{border-radius:18px!important}.start-dialog-header{padding:14px}.start-dialog-body{padding:14px!important}.otp-keypad{gap:8px}.keypad-key{height:58px!important}.start-dialog-actions{padding:12px 14px!important;position:sticky;bottom:0}.start-dialog-actions .v-btn:last-child{min-height:48px}.verification-actions{align-items:stretch;flex-direction:column;gap:4px}.verification-actions .v-btn{width:100%}}
</style>
